import React, { useState, useRef, useEffect } from "react";
import { Upload, Button, message, Progress } from "antd";
import { CheckCircleOutlined } from "@ant-design/icons";
import uploadBtn from "../../../../../assets/upload-btn.svg";
import sampleFile from "../../../../../assets/sampleCsv.csv";
import CommonModal from "../../../../../components/common-modal";
import { useBulkUpload } from "hooks/configuration/employee/employee";
import { useNavigate } from "react-router-dom";

const BulkUploadModal = ({ open, onClose, showErrorMessage, errorMessage }) => {
  const navigate = useNavigate();
  const [files, setFiles] = useState(null);
  const [, setResponseData] = useState([]);
  const [currentProgress, setCurrentProgress] = useState(0);
  // "active" while uploading/verifying, "success" when verified, "exception" when rejected.
  const [progressStatus, setProgressStatus] = useState("normal");
  const [buildUploadList, setBuildUploadList] = useState([]);
  const [serverErrors, setServerErrors] = useState({});

  const uploadRef = useRef(null);

  const { mutate: uploadCSV, isLoading: uploading } = useBulkUpload();

  const uploadProps = {
    name: "file",
    multiple: false,
    accept: ".csv",
    onChange(info) {
      const selectedFile = info?.file?.originFileObj;
      if (!selectedFile) return;
      const isCsv =
        selectedFile.type === "text/csv" || selectedFile.name.endsWith(".csv");
      if (isCsv) {
        setFiles(selectedFile);
        setResponseData([]);
        setServerErrors({});
        setProgressStatus("normal");
        // The file is in hand but nothing has been sent yet, so the bar shows full and
        // grey -- "loaded, ready to verify". Left at 0 it read as a stalled upload.
        setCurrentProgress(100);
      } else {
        message.error("You can only upload CSV files!");
      }
    },
    // Selecting a file only holds it locally; the real upload happens on Verify.
    // (This used to post to a placeholder URL, so the progress bar never moved.)
    customRequest: ({ onSuccess }) => {
      onSuccess("ok");
    },
  };

  const closeModal = () => {
    setFiles(null);
    setResponseData([]);
    setServerErrors({});
    setProgressStatus("normal");
    setCurrentProgress(0); // Reset progress bar when closing modal
    onClose();
  };

  
  const CsvUpload = () => {
    const formData = new FormData();
    if (files !== null) {
      formData.append("csvFile", files);
    }
    setCurrentProgress(0);
    setServerErrors({});
    setProgressStatus("active");
    const onUploadProgress = (event) => {
      if (event.total) {
        // Hold at 99 until the server has read the file and answered.
        setCurrentProgress(Math.min(99, Math.round((event.loaded / event.total) * 100)));
      }
    };
    uploadCSV({ formData, onUploadProgress }, {
      onSuccess: (item) => {
        if (item?.statusCode === 200) {
          setCurrentProgress(100);
          setProgressStatus("success");
          setBuildUploadList((prevListData) => {
            const newItems = item?.data || [];
            return [...new Set([...prevListData, ...newItems])];
          });
        } else {
          // Keep the bar where it stopped and mark it failed, rather than dropping to 0.
          setProgressStatus("exception");
          message.error(item?.message);
        }
      },
      onError: (error) => {
        setProgressStatus("exception");
        const errorMessages = error?.response?.data?.errorMessages;
        if (errorMessages) {
          // Stays until the next file is chosen; it used to vanish after 3s.
          setServerErrors(errorMessages);
        } else {
          setServerErrors({ CSV: [error?.response?.data?.message || "Upload failed. Please try again."] });
        }
      },
    });
  };


  useEffect(() => {
    if (buildUploadList.length !== 0) {
      navigate("/configuration/employee/bulk-upload", { state: buildUploadList });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildUploadList])


  return (
    <CommonModal
      open={open}
      onClose={closeModal}
      title="Bulk upload"
      subTitle="Upload and Add Employee."
      sampleFile={sampleFile}
      content={
        <div>
          <div className="dropzone">
            <Upload ref={uploadRef} {...uploadProps} maxCount={1} showUploadList={false}>
              {files ? (
                <div>
                  <img src={uploadBtn} alt="" />
                  <h3>{files.name}</h3>
                </div>
              ) : (
                <>
                  <img src={uploadBtn} alt="" />
                  <p>
                    <span>Click to upload</span> or drag and drop <br /> Excel, CSV
                  </p>
                </>
              )}
            </Upload>
          </div>

          {files && (
            <div className="file-container">
              <div className="file-details">
                <div className="file-doc-type">
                  <CheckCircleOutlined />
                  <div className="file-name">
                    <h3>{files.name}</h3>
                    <p>{(files.size / 1024).toFixed(2)} KB</p>
                  </div>
                </div>
              </div>
              <div className="progress-container">
                <Progress
                  percent={currentProgress}
                  status={progressStatus}
                  strokeWidth={5}
                  // The modal's blue, so Ready and Uploading match the rest of the dialog
                  // rather than antd's default. Verified/Failed keep antd's green and red.
                  strokeColor={
                    progressStatus === "success" || progressStatus === "exception"
                      ? undefined
                      : "#3166FF"
                  }
                  // The label below carries the percentage, so antd's own text would
                  // only repeat it.
                  showInfo={false}
                />
                <p>
                  {progressStatus === "success" ? "Verified"
                    : progressStatus === "exception" ? "Failed"
                    : progressStatus === "active" ? (currentProgress >= 99 ? "Verifying…" : `Uploading… ${currentProgress}%`)
                    : "Ready"}
                </p>
              </div>
            </div>
          )}

          {serverErrors?.CSV && <p className="error-message">{[].concat(serverErrors.CSV).join(" ")}</p>}

          <div className="modal-btns">
            <Button
              type="default"
              size="large"
              className="modal-cancel-btn"
              onClick={closeModal}
            >
              Cancel
            </Button>
          {files === null ?  <Upload ref={uploadRef} {...uploadProps} maxCount={1} showUploadList={false}>
              <Button
                type="primary"
                size="large"
                className="modal-upload-button"
              >
                {files === null ? "Upload" : "Verify"}
              </Button>
            </Upload>
            
            :    <Button
                type="primary"
                size="large"
                className="modal-verify-button"
                loading={uploading}
                onClick={() => CsvUpload()}
              >
                {files === null ? "Upload" : "Verify"}
              </Button>}
          </div>
        </div>
      }
    />
  );
};

export default BulkUploadModal;
